import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Logo } from './Brand';
import { useAuth } from '../AuthContext';
import { ThemeToggle } from './ThemeToggle';
import {
  LayoutDashboard, Dog, Calendar, ShoppingCart, Users, FileBarChart, Heart,
  MessagesSquare, LogOut, BookOpen, UserCircle, GraduationCap, MessageCircle,
  FileSignature, Image as ImageIcon, Megaphone, PenSquare, LineChart, Share2, GraduationCap as GradCap, Video, Radio,
  BookOpenCheck,
} from 'lucide-react';
import { Button } from './ui/button';

const ClientLinks = [
  { to: '/client', label: 'Dashboard', icon: LayoutDashboard, testid: 'side-client-dashboard' },
  { to: '/client/dogs', label: 'My Dogs', icon: Dog, testid: 'side-client-dogs' },
  { to: '/client/book', label: 'Book Boarding', icon: Calendar, testid: 'side-client-book' },
  { to: '/client/bookings', label: 'My Bookings', icon: BookOpen, testid: 'side-client-bookings' },
  { to: '/client/courses', label: 'My Courses', icon: BookOpenCheck, testid: 'side-client-courses' },
  { to: '/client/messages', label: 'Messages', icon: MessageCircle, testid: 'side-client-messages' },
  { to: '/client/gallery', label: 'My Gallery', icon: ImageIcon, testid: 'side-client-gallery' },
  { to: '/client/contracts', label: 'Contracts', icon: FileSignature, testid: 'side-client-contracts' },
  { to: '/client/profile', label: 'My Profile', icon: UserCircle, testid: 'side-client-profile' },
  { to: '/client/membership', label: 'Memberships', icon: Heart, testid: 'side-client-membership' },
  { to: '/community', label: 'Community', icon: MessagesSquare, testid: 'side-community' },
];

const AdminLinks = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, testid: 'side-admin-dashboard' },
  { to: '/admin/run-grid', label: 'Run Grid', icon: Calendar, testid: 'side-admin-grid' },
  { to: '/admin/trainer-grid', label: 'Trainer Grid', icon: GraduationCap, testid: 'side-admin-trainer-grid' },
  { to: '/admin/training', label: 'Training Bookings', icon: GraduationCap, testid: 'side-admin-training' },
  { to: '/admin/bookings', label: 'Boarding', icon: BookOpen, testid: 'side-admin-bookings' },
  { to: '/admin/clients', label: 'Clients & Dogs', icon: Users, testid: 'side-admin-clients' },
  { to: '/admin/pos', label: 'POS Terminal', icon: ShoppingCart, testid: 'side-admin-pos' },
  { to: '/admin/messages', label: 'Client Messages', icon: MessageCircle, testid: 'side-admin-messages' },
  { to: '/admin/gallery', label: 'Gallery Push', icon: ImageIcon, testid: 'side-admin-gallery' },
  { to: '/admin/marketing', label: 'Marketing', icon: Megaphone, testid: 'side-admin-marketing' },
  { to: '/admin/blog', label: 'Blog & AI Images', icon: PenSquare, testid: 'side-admin-blog' },
  { to: '/admin/team-chat', label: 'Team Chat', icon: MessageCircle, testid: 'side-admin-team-chat' },
  { to: '/admin/reports', label: 'Reports', icon: FileBarChart, testid: 'side-admin-reports' },
  { to: '/admin/analytics', label: 'Live Analytics', icon: LineChart, testid: 'side-admin-analytics' },
  { to: '/admin/social', label: 'Social Hub', icon: Share2, testid: 'side-admin-social' },
  { to: '/admin/courses', label: 'Courses', icon: GradCap, testid: 'side-admin-courses' },
  { to: '/admin/video-calls', label: 'Video Calls', icon: Video, testid: 'side-admin-video-calls' },
  { to: '/admin/live-events', label: 'Live Events', icon: Radio, testid: 'side-admin-live-events' },
  { to: '/community', label: 'Community', icon: MessagesSquare, testid: 'side-admin-community' },
];

const TrainerLinks = [
  { to: '/admin', label: 'My Dogs', icon: Dog, testid: 'side-trainer-dashboard' },
  { to: '/admin/training', label: 'My Training', icon: GraduationCap, testid: 'side-trainer-training' },
  { to: '/admin/trainer-grid', label: 'Trainer Grid', icon: Calendar, testid: 'side-trainer-grid' },
  { to: '/admin/clients', label: 'Clients & Dogs', icon: Users, testid: 'side-trainer-clients' },
  { to: '/admin/messages', label: 'Client Messages', icon: MessageCircle, testid: 'side-trainer-messages' },
  { to: '/admin/gallery', label: 'Gallery Push', icon: ImageIcon, testid: 'side-trainer-gallery' },
  { to: '/admin/team-chat', label: 'Team Chat', icon: MessageCircle, testid: 'side-trainer-team-chat' },
  { to: '/community', label: 'Post to Community', icon: MessagesSquare, testid: 'side-trainer-community' },
];

export const Sidebar = ({ variant = 'client' }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  let links = ClientLinks;
  if (variant === 'admin') links = AdminLinks;
  if (variant === 'trainer') links = TrainerLinks;
  return (
    <aside
      className='jfk-sidebar w-[260px] flex-shrink-0 border-r flex flex-col bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-100 hidden lg:flex'
      data-testid='sidebar'
    >
      <div className='h-16 flex items-center justify-between px-5 border-b border-slate-200 dark:border-slate-800'>
        <Logo />
        <ThemeToggle size='sm' />
      </div>
      <nav className='flex-1 p-3 space-y-1 overflow-y-auto'>
        {links.map((l) => (
          <NavLink
            key={l.to + l.label}
            to={l.to}
            end={l.to === '/client' || l.to === '/admin'}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-[#1B3A87] text-white'
                  : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`
            }
            data-testid={l.testid}
          >
            <l.icon className='w-4 h-4' />
            <span>{l.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className='p-3 border-t border-slate-200 dark:border-slate-800'>
        <div className='flex items-center gap-3 px-2 py-2'>
          <div className='w-9 h-9 rounded-full flex items-center justify-center font-semibold bg-[#1B3A87] text-white'>
            {user?.full_name?.[0]?.toUpperCase() || 'U'}
          </div>
          <div className='flex-1 min-w-0'>
            <div className='text-sm font-semibold truncate'>{user?.full_name}</div>
            <div className='text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400'>{user?.role}</div>
          </div>
        </div>
        <Button
          variant='ghost'
          size='sm'
          className='w-full justify-start mt-1 dark:text-slate-200 dark:hover:bg-slate-800'
          onClick={() => { logout(); navigate('/'); }}
          data-testid='sidebar-logout-button'
        >
          <LogOut className='w-4 h-4 mr-2' /> Sign out
        </Button>
      </div>
    </aside>
  );
};
